import React, { useContext, useEffect, useState } from "react";

import { useAppStore } from "../../../../context/store";
import { Dialog, Portal, Select, CloseButton, Button, createListCollection, Field, Input, Box, HStack, Spinner } from "@chakra-ui/react";
import { LuKey, LuMail, LuPlus, LuShield, LuSquarePen, LuText, LuUser } from "react-icons/lu";
import Type from "../../../../utilies/Types";
import { serviceAddUsers, serviceEditUsers, serviceGetUsers } from "../../../../api/services/user";
import { floatingStyles, showToast } from "../../../../utilies/Utility";

export const UsersAndTeamManagementNewUserDialog = () => {
  // state
  const showNewUserDialog = useAppStore((s) => s.showNewUserDialog);
  const selectUserForEdit = useAppStore((s) => s.selectUserForEdit);

  // setters
  const setShowNewUserDialog = useAppStore.getState().setShowNewUserDialog;
  const setSelectUserForEdit = useAppStore.getState().setSelectUserForEdit;
  const setUsers = useAppStore.getState().setUsers;

  const [isProgress, setIsProgress] = useState(false);

  const [sendingData, setSendingData] = useState({
    username: "",
    password: "",
    email: "",
    first_name: "",
    last_name: "",
    role: [],
  });

  useEffect(() => {
    if (showNewUserDialog && selectUserForEdit) {
      setSendingData({
        username: selectUserForEdit.username,
        password: "",
        email: selectUserForEdit.email,
        first_name: selectUserForEdit.first_name,
        last_name: selectUserForEdit.last_name,
        role: selectUserForEdit.roles,
      });
    }
    if (!showNewUserDialog) {
      setSelectUserForEdit(null);
    }
  }, [showNewUserDialog]);

  const roles = createListCollection({
    items: [
      { label: Type.ROLE.SUPER_ADMIN, value: Type.ROLE.SUPER_ADMIN },
      { label: Type.ROLE.ADMIN, value: Type.ROLE.ADMIN },
      { label: Type.ROLE.DIGITIZER, value: Type.ROLE.DIGITIZER },
      { label: Type.ROLE.ANNOTATOR, value: Type.ROLE.ANNOTATOR },
      { label: Type.ROLE.REVIEWER, value: Type.ROLE.REVIEWER },
      { label: Type.ROLE.VIEWER, value: Type.ROLE.VIEWER },
    ],
  });

  const addUser = () => {
    const formData = new FormData();
    formData.append("username", sendingData.username);
    formData.append("password", sendingData.password);
    formData.append("email", sendingData.email);
    formData.append("first_name", sendingData.first_name);
    formData.append("last_name", sendingData.last_name);
    formData.append("role", sendingData.role.join(","));

    setIsProgress(true);
    serviceAddUsers(formData)
      .then((res) => {
        setIsProgress(false);
        if (res.data.status == "failed") {
          showToast(res.data.message, Type.NOTIFICATION.ERROR);
        } else {
          getUsers();
          showToast("User added", Type.NOTIFICATION.SUCCESS);
          setShowNewUserDialog(false);
        }
      })
      .catch((err) => {
        console.error("Error", err);
        showToast(err.message || "Request failed", Type.NOTIFICATION.ERROR);
        setIsProgress(false);
      });
  };

  const editUser = () => {
    const formData = new FormData();
    let temp = {
      username: sendingData.username,
      first_name: sendingData.first_name,
      last_name: sendingData.last_name,
      email: sendingData.email,
      role: sendingData.role.join(","),
    };
    if (sendingData.password) {
      temp.password = sendingData.password;
    }

    formData.append("data", JSON.stringify(temp));

    setIsProgress(true);
    serviceEditUsers(formData)
      .then((res) => {
        setIsProgress(false);
        if (res.data.status == "failed") {
          showToast(res.data.message, Type.NOTIFICATION.ERROR);
        } else {
          getUsers();
          showToast("User updated", Type.NOTIFICATION.SUCCESS);
          setShowNewUserDialog(false);
        }
      })
      .catch((err) => {
        console.error("Error", err);
        showToast(err.message || "Request failed", Type.NOTIFICATION.ERROR);
        setIsProgress(false);
      });
  };

  const getUsers = () => {
    serviceGetUsers()
      .then((res) => {
        setUsers(res.data);
      })
      .catch((err) => {
        console.error("Error", err);
      });
  };

  return (
    <Dialog.Root lazyMount open={showNewUserDialog} closeOnInteractOutside={false} onOpenChange={(e) => setShowNewUserDialog(e.open)}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{selectUserForEdit ? "Edit" : "New"} User</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <HStack gap={2} mb={5}>
                <Field.Root>
                  <Box pos="relative" w="full">
                    <Input
                      className="peer"
                      placeholder=""
                      type="text"
                      value={sendingData?.first_name}
                      onChange={(e) => {
                        setSendingData((prev) => ({ ...prev, first_name: e.target.value }));
                      }}
                    />
                    <Field.Label css={floatingStyles}>
                      <LuText /> First Name
                    </Field.Label>
                  </Box>
                </Field.Root>
                <Field.Root>
                  <Box pos="relative" w="full">
                    <Input
                      className="peer"
                      placeholder=""
                      type="text"
                      value={sendingData?.last_name}
                      onChange={(e) => {
                        setSendingData((prev) => ({ ...prev, last_name: e.target.value }));
                      }}
                    />
                    <Field.Label css={floatingStyles}>
                      <LuText /> Last Name
                    </Field.Label>
                  </Box>
                </Field.Root>
              </HStack>

              <Field.Root>
                <Box pos="relative" w="full">
                  <Input
                    className="peer"
                    placeholder=""
                    type="text"
                    disabled={selectUserForEdit ? true : false}
                    value={sendingData?.username}
                    onChange={(e) => {
                      setSendingData((prev) => ({ ...prev, username: e.target.value }));
                    }}
                  />
                  <Field.Label css={floatingStyles}>
                    <LuUser /> Username
                  </Field.Label>
                </Box>
              </Field.Root>

              <Field.Root mt={3}>
                <Box pos="relative" w="full">
                  <Input
                    className="peer"
                    placeholder=""
                    type="text"
                    value={sendingData?.email}
                    onChange={(e) => {
                      setSendingData((prev) => ({ ...prev, email: e.target.value }));
                    }}
                  />
                  <Field.Label css={floatingStyles}>
                    <LuMail /> Email
                  </Field.Label>
                </Box>
              </Field.Root>

              <Field.Root mt={3}>
                <Box pos="relative" w="full">
                  <Input
                    className="peer"
                    placeholder=""
                    type="text"
                    value={sendingData?.password}
                    onChange={(e) => {
                      setSendingData((prev) => ({ ...prev, password: e.target.value }));
                    }}
                  />
                  <Field.Label css={floatingStyles}>
                    <LuKey /> Password
                  </Field.Label>
                </Box>
              </Field.Root>

              <Select.Root
                multiple
                collection={roles}
                size="sm"
                width="full"
                mt={3}
                mb={3}
                value={sendingData?.role}
                onValueChange={(val) => {
                  setSendingData((prev) => ({ ...prev, role: val.value }));
                }}
              >
                <Select.HiddenSelect />
                <Select.Label>
                  <HStack>
                    <LuShield /> Role
                  </HStack>
                </Select.Label>
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder="Select Contributors" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.ClearTrigger />
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>

                <Select.Positioner>
                  <Select.Content>
                    {roles.items.map((framework) => (
                      <Select.Item item={framework} key={framework.value}>
                        {framework.label}
                        <Select.ItemIndicator />
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline">Cancel</Button>
              </Dialog.ActionTrigger>
              <Button colorPalette={"green"} onClick={selectUserForEdit ? editUser : addUser} disabled={isProgress}>
                {isProgress ? (
                  <Spinner size={"sm"} />
                ) : selectUserForEdit ? (
                  <>
                    Edit <LuSquarePen />
                  </>
                ) : (
                  <>
                    Add <LuPlus />
                  </>
                )}
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default UsersAndTeamManagementNewUserDialog;
